import { createSelector } from 'reselect'
import detect from 'detect.js'
import moment from 'moment-timezone'
import { parseQuery } from '@calendly/http-tools'

import {
  timezoneSelector,
  timeNotationSelector,
  selectedLocationConfigurationSelector,
  eventSelector
} from 'selectors'
import { featuresSelector } from 'root-api'
import { isDialCodeOnly } from 'services/phone_number'

const BOOKING_FLOW_VERSION = 'v3'

const schedulingSelector = ({ scheduling }) => scheduling
const analyticsSelector = ({ analytics }) => analytics

export const autofillDataSelector = ({ scheduling }) => scheduling.autofillData
export const showExternalRedirectSelector = ({ scheduling }) => scheduling.showExternalRedirect

export const filledFieldsSelector = createSelector(
  schedulingSelector,
  (scheduling) => scheduling.fields_filled
)

const fieldsCountSelector = createSelector(
  schedulingSelector,
  (scheduling) => scheduling.fields_count
)

export const analyticsDataSelector = createSelector(
  fieldsCountSelector,
  filledFieldsSelector,
  analyticsSelector,
  (fieldsCount, filledFields, analytics) => {
    const ua = detect.parse(navigator.userAgent)
    const params = new URLSearchParams(window.location.search)
    const analyticsShareAttribution = params.get('share_attribution')

    const result = Object.assign({}, analytics, {
      browser: ua.browser.name,
      device: `${ua.device.name} ${ua.os.name}`,
      fields_filled: filledFields.length,
      fields_presented: fieldsCount,
      booking_flow: BOOKING_FLOW_VERSION,
      seconds_to_convert: moment().diff(analytics.invitee_landed_at, 'seconds')
    })

    if (analyticsShareAttribution) {
      result.from = analyticsShareAttribution
    }

    return result
  }
)

export const notificationTypeSelector = createSelector(
  eventSelector,
  ({ active_notification_type: notificationType }) => notificationType
)

export const embedSelector = (state) => {
  const features = featuresSelector(state)

  const { custom_theme_allowed: customThemeAllowed } = features || {}

  const {
    embed_domain: embedDomain,
    embed_type: embedType,
    hide_event_type_details: hideEventTypeDetails,
    hide_landing_page_details: hideLandingPageDetails,
    primary_color: primaryColor,
    background_color: backgroundColor,
    text_color: textColor
  } = parseQuery(window.location.search)
  let hideDetails
  let colorCustomization
  const anyColor = Boolean(primaryColor) || Boolean(backgroundColor) || Boolean(textColor)

  if (embedDomain && embedType) {
    hideDetails = !!(hideEventTypeDetails || hideLandingPageDetails)
    colorCustomization = customThemeAllowed && anyColor
  }

  return {
    domain: embedDomain,
    type: embedType,
    hide_details: hideDetails,
    color_customization: colorCustomization
  }
}

export const formSubmissionUuidSelector = () => {
  const { submission_uuid: submissionUUid } = parseQuery(window.location.search)

  return submissionUUid
}

export const purchasedPackageUuidSelector = () => {
  const { redeem } = parseQuery(window.location.search)

  return redeem
}

const locationConfigurationSelector = createSelector(
  selectedLocationConfigurationSelector,
  ({ kind, location, data, ...locationConfiguration }) => ({
    kind,
    location,
    data,
    phone_number: locationConfiguration.phone_number,
    additional_info: locationConfiguration.additional_info
  })
)

export const schedulingEventSelector = (state, { spotStartTime, durationOverride, formData }) => {
  const emails = formData.event.guest_emails

  return {
    start_time: spotStartTime,
    duration_override: durationOverride,
    location_configuration: locationConfigurationSelector(state),
    guests: (emails && emails.length > 0) ? emails.map((email) => ({ email })) : []
  }
}

export const inviteeDataSelector = (state, { formData: { invitee } }) => (
  Object.values(invitee).reduce((memo, props) => {
    // Filter out dialing-code-only phone numbers
    if (props.name === 'phone_number' && props.value && props.countryCode) {
      if (isDialCodeOnly(props.value, props.countryCode)) {
        return memo
      }
    }

    return props.value
      ? { ...memo, [props.name]: props.value }
      : memo
  },
  {
    external_communication_consent: invitee.external_communication_consent,
    uuid: parseQuery(window.location.search).uuid,
    timezone: timezoneSelector(state),
    time_notation: timeNotationSelector(state)
  })
)

export const showRecaptchaChallengeSelector = createSelector(schedulingSelector, (scheduling) => scheduling.recaptcha_challenge?.show)
export const failedRecaptchaChallengeSelector = createSelector(schedulingSelector, (scheduling) => scheduling.recaptcha_challenge?.failed)
export const recaptchaRequestIdSelector = createSelector(
  schedulingSelector,
  (scheduling) => scheduling.recaptcha_challenge?.request_id
)

export const showVerificationCodeChallengeSelector = createSelector(
  schedulingSelector,
  (scheduling) => scheduling.verification_code_challenge?.show
)
export const verificationCodeErrorSelector = createSelector(
  schedulingSelector,
  (scheduling) => scheduling.verification_code_challenge?.error_message
)
export const schedulingParamsSelector = createSelector(
  schedulingSelector,
  (scheduling) => scheduling.verification_code_challenge?.scheduling_params
)

export const customFieldsSelector = createSelector(
  schedulingSelector,
  (scheduling) => scheduling.builtEventCustomFields
)

export const isBookingAfterStripeRedirectSelector = createSelector(
  schedulingSelector,
  (scheduling) => scheduling.isBookingAfterStripeRedirect
)

export const savedFormDataForStripeRedirectSelector = createSelector(
  schedulingSelector,
  (scheduling) => scheduling.savedFormDataForStripeRedirect
)
